import {
  LabsBreadcrumbs,
  OrderDesignPreview,
  GetOrderByUniqueAttributeDocument,
} from "@jasper/shared";
import {
  Alert,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  ScrollArea,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { UseAuth } from "../../contexts";
import { useQuery } from "@apollo/client";
import { useOrderDesignsList } from "@jasper/use-cases";
import {
  OrderDesignStatus,
  OrderDesignStatusEnum,
} from "@jasper/client-models";
import { useEffect, useState } from "react";
import { OrderDesignHistoryTab } from "./OrderDesignHistoryTab";
import { OrderDesignPreviewBottomOverlay } from "./OrderDesignPreviewBottomOverlay";
import { OrderDesignCommentTab } from "./OrderDesignCommentTab";

const getStatusColor = (status: OrderDesignStatus) => {
  switch (status) {
    case OrderDesignStatusEnum.REQUESTED:
    case OrderDesignStatusEnum.DRAFT:
      return "gray";
    case OrderDesignStatusEnum.OPENED:
      return "blue";
    case OrderDesignStatusEnum.VALIDATED:
    case OrderDesignStatusEnum.AUTOMATICALLY_VALIDATED:
      return "green";
    case OrderDesignStatusEnum.REJECTED:
      return "red";
    default:
      return "gray";
  }
};

const OrderDesigns = ({ orderId }: { orderId: string }) => {
  const [activeDesignId, setActiveDesignId] = useState<string>();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = UseAuth();
  const { data, loading: orderLoading } = useQuery(
    GetOrderByUniqueAttributeDocument,
    { variables: { where: { id: orderId } }, fetchPolicy: "cache-first" }
  );
  const { orderDesigns, isLoading: orderDesignsLoading } = useOrderDesignsList({
    orderId,
  });

  const selectActiveDesign = (designId: string) => {
    setActiveDesignId(designId);
    searchParams.set("activeDesignId", designId);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  useEffect(() => {
    if (orderDesigns.length > 0 && !activeDesignId) {
      const designIdFromUrl = searchParams.get("activeDesignId");
      if (
        designIdFromUrl &&
        orderDesigns.some(design => design.id === designIdFromUrl)
      ) {
        selectActiveDesign(designIdFromUrl);
        return;
      }

      selectActiveDesign(orderDesigns[0].id);
    }
  }, [orderDesigns, activeDesignId, searchParams]);

  if (orderLoading || orderDesignsLoading) {
    return (
      <Stack w="100%" align="center" justify="center">
        <Loader />
      </Stack>
    );
  }

  const activeOrderDesign = orderDesigns.find(
    design => design.id === activeDesignId
  );

  return (
    <Stack gap="xl" h="80dvh">
      <LabsBreadcrumbs
        orderReference={data?.getOrderByUniqueAttribute.id}
        orderId={orderId}
        user={user}
        searchText=""
      />
      <Card withBorder h="100%" p="0">
        {orderDesigns.length === 0 ? (
          <Center h="100%" p="xl">
            <Alert color="blue" title="No design">
              No design is available for this order yet.
            </Alert>
          </Center>
        ) : (
          <Group align="flex-start" h="100%" gap="0">
            <SegmentedControl
              radius={0}
              orientation="vertical"
              value={activeDesignId}
              onChange={selectActiveDesign}
              data={orderDesigns.map(design => ({
                value: design.id,
                label: (
                  <Group key={design.id}>
                    <Badge color={getStatusColor(design.status)}>
                      {design.status}
                    </Badge>
                    <Text>
                      {new Date(design.createdAt).toLocaleDateString()}
                    </Text>
                  </Group>
                ),
              }))}
            />
            <Box flex={3} h="100%">
              {activeDesignId && activeOrderDesign && (
                <Card shadow="xl" h="100%" p={0}>
                  <Group
                    h="100%"
                    gap="0"
                    justify="flex-start"
                    align="flex-start"
                  >
                    <Box flex={3} w="75%" h="100%">
                      <OrderDesignPreview
                        borderRadius={0}
                        bottomOverlay={{
                          render: props => (
                            <OrderDesignPreviewBottomOverlay {...props} />
                          ),
                        }}
                        orderDesignId={activeDesignId}
                        interactionsEnabled={true}
                      />
                    </Box>
                    <Stack flex={1} h="100%" mih={0}>
                      <Tabs
                        defaultValue="comments"
                        h="100%"
                        style={{ display: "flex", flexDirection: "column" }}
                      >
                        <Tabs.List grow>
                          <Tabs.Tab value="comments">Comments</Tabs.Tab>
                          <Tabs.Tab value="history">History</Tabs.Tab>
                        </Tabs.List>

                        <ScrollArea
                          type="auto"
                          style={{ flex: 1, minHeight: 0 }}
                        >
                          <OrderDesignCommentTab
                            comments={activeOrderDesign.comments}
                            tab="comments"
                          />
                          <OrderDesignHistoryTab
                            history={activeOrderDesign.history}
                            tab="history"
                          />
                        </ScrollArea>
                      </Tabs>
                    </Stack>
                  </Group>
                </Card>
              )}
            </Box>
          </Group>
        )}
      </Card>
    </Stack>
  );
};

export const OrderDesignsPage = () => {
  const { orderId } = useParams<{ orderId: string }>();

  if (!orderId) {
    return <div>Order ID is required</div>;
  }

  return <OrderDesigns orderId={orderId} />;
};
